import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Search,
  Clock,
  Users,
  BookOpen,
  ImageIcon,
  Plus,
  Filter,
  X,
  Pencil,
  Trash,
  Eye,
  RefreshCw
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import LayoutSwitcher, { LayoutType } from "@/components/layout/Training/LayoutSwitcher";
import { RootState } from "@/store";
import {
  fetchMyTrainings,
  setMyTrainingsFilter,
  resetMyTrainingsFilter,
  deleteTraining
} from "@/features/training/trainingSlice";
import { Training, TrainingFilter } from "@/features/training/types";
import { useQuery } from "@apollo/client";
import { GET_TRAINING_IMAGE } from "@/lib/graphql/queries";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// TrainingImage Component
const TrainingImage = ({ filename, alt, className }: { filename: string; alt: string; className: string }) => {
  const { loading, error, data } = useQuery(GET_TRAINING_IMAGE, {
    variables: { filename },
    skip: !filename || filename.startsWith("http"),
  });

  if (loading) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 animate-pulse`}>
        <ImageIcon className="text-gray-400" aria-hidden="true" />
      </div>
    );
  }

  if (error || !data) {
    console.error("Error loading image:", error?.message || "No data");
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <ImageIcon className="text-gray-400" aria-hidden="true" />
      </div>
    );
  }

  const src = filename.startsWith("http")
    ? filename
    : `data:${data.getTrainingImageByName?.mimetype};base64,${data.getTrainingImageByName?.base64Data}`;

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} object-cover`}
      loading="lazy"
    />
  );
};

const MyTrainings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    myTrainingsList,
    myTrainingsPaginationMeta,
    myTrainingsLoading,
    myTrainingsError,
    myTrainingsFilter,
    deleteLoading
  } = useSelector((state: RootState) => state.training);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedFormat, setSelectedFormat] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [layout, setLayout] = useState<LayoutType>("grid");
  const [trainingToDelete, setTrainingToDelete] = useState<Training | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Categories, levels, and formats for filters
  const categories = [
    "Programming",
    "Data Science",
    "Business",
    "Design",
    "Marketing",
    "Personal Development",
  ];
  const levels = ["Beginner", "Intermediate", "Advanced"];
  const formats = ["Online", "In-Person", "Hybrid"];

  // Fetch trainings on component mount
  useEffect(() => {
    dispatch(fetchMyTrainings(myTrainingsFilter) as any);
  }, [dispatch, myTrainingsFilter]);

  // Search handler
  const handleSearch = () => {
    dispatch(
      setMyTrainingsFilter({ ...myTrainingsFilter, search: searchTerm, page: 1 })
    );
    dispatch(fetchMyTrainings({ ...myTrainingsFilter, search: searchTerm, page: 1 }) as any);
  };

  // Category filter handler
  const handleCategoryFilter = (value: string) => {
    setSelectedCategory(value);
    const category = value === "ALL" ? "" : value;
    dispatch(setMyTrainingsFilter({ ...myTrainingsFilter, category, page: 1 }));
    dispatch(
      fetchMyTrainings({ ...myTrainingsFilter, category, page: 1 }) as any
    );
  };

  // Level filter handler
  const handleLevelFilter = (value: string) => {
    setSelectedLevel(value);
    const level = value === "ALL" ? "" : value;
    dispatch(setMyTrainingsFilter({ ...myTrainingsFilter, level, page: 1 }));
    dispatch(fetchMyTrainings({ ...myTrainingsFilter, level, page: 1 }) as any);
  };

  // Format filter handler
  const handleFormatFilter = (value: string) => {
    setSelectedFormat(value);
    const format = value === "ALL" ? "" : value;
    dispatch(setMyTrainingsFilter({ ...myTrainingsFilter, format, page: 1 }));
    dispatch(fetchMyTrainings({ ...myTrainingsFilter, format, page: 1 }) as any);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedLevel("");
    setSelectedFormat("");
    dispatch(resetMyTrainingsFilter());
    dispatch(
      fetchMyTrainings({
        search: "",
        category: "",
        level: "",
        format: "",
        certificate: null,
        location: "",
        page: 1,
        limit: 10,
      } as TrainingFilter) as any
    );
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    dispatch(setMyTrainingsFilter({ ...myTrainingsFilter, page }));
    dispatch(fetchMyTrainings({ ...myTrainingsFilter, page }) as any);
  };

  // Handle training click
  const handleTrainingClick = (id: number) => {
    navigate(`/my-trainings/${id}`);
  };

  // Handle edit training
  const handleEditTraining = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/admin/trainings/edit/${id}`);
  };

  // Handle delete training
  const handleDeleteClick = (training: Training, e: React.MouseEvent) => {
    e.stopPropagation();
    setTrainingToDelete(training);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!trainingToDelete) return;

    try {
      await dispatch(deleteTraining(trainingToDelete.id) as any).unwrap();
      toast({
        title: "Training deleted",
        description: `${trainingToDelete.title} has been successfully deleted.`,
        variant: "default",
      });
      // Refresh the list
      dispatch(fetchMyTrainings(myTrainingsFilter) as any);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to delete training: ${error}`,
        variant: "destructive",
      });
    } finally {
      setShowDeleteDialog(false);
      setTrainingToDelete(null);
    }
  };

  // Handle create new training
  const handleCreateTraining = () => {
    navigate("/admin/trainings/create");
  };

  // Render training card based on layout
  const renderTraining = (training: Training) => {
    switch (layout) {
      case "grid":
        return (
          <Card
            key={training.id}
            className="flex flex-col hover:shadow-lg transition-all duration-300 cursor-pointer group"
            onClick={() => handleTrainingClick(training.id)}
          >
            <div className="relative">
              <TrainingImage
                filename={training.image}
                alt={training.title}
                className="w-full h-48 object-cover rounded-t-lg"
              />
              <Badge
                variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                className="absolute top-2 right-2"
              >
                {training.format}
              </Badge>
            </div>
            <CardHeader className="flex-grow">
              <CardTitle className="line-clamp-2">{training.title}</CardTitle>
              <CardDescription className="line-clamp-2 mt-2">
                {training.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="outline">{training.category}</Badge>
                <Badge variant="outline">{training.level}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">{training.price} TND</span>
              </div>
            </CardContent>
            <CardFooter className="border-t p-3 bg-muted/20 flex justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/my-trainings/${training.id}`);
                }}
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => handleEditTraining(training.id, e)}
              >
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                onClick={(e) => handleDeleteClick(training, e)}
              >
                <Trash className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </CardFooter>
          </Card>
        );
      case "list":
        return (
          <Card
            key={training.id}
            className="overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group"
            onClick={() => handleTrainingClick(training.id)}
          >
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/3 relative">
                <TrainingImage
                  filename={training.image}
                  alt={training.title}
                  className="h-48 md:h-full w-full object-cover"
                />
                <Badge
                  variant={training.format.toLowerCase() === "online" ? "default" : "secondary"}
                  className="absolute top-2 right-2"
                >
                  {training.format}
                </Badge>
              </div>
              <div className="flex-1 p-6">
                <CardTitle className="mb-2">{training.title}</CardTitle>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">{training.category}</Badge>
                    <Badge variant="outline">{training.level}</Badge>
                  </div>
                  <span className="font-semibold">{training.price} TND</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{training.description}</p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/my-trainings/${training.id}`);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => handleEditTraining(training.id, e)}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                    onClick={(e) => handleDeleteClick(training, e)}
                  >
                    <Trash className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Trainings</h1>
          <p className="text-muted-foreground mt-1">Manage your created training courses</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5"
          >
            {showFilters ? <X className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
            {showFilters ? "Hide Filters" : "Filters"}
          </Button>
          <Button
            onClick={handleCreateTraining}
            size="sm"
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Create Training
          </Button>
        </div>
      </div>

      {/* Search and Layout Switcher */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6 items-start sm:items-center">
        <div className="flex-1 relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            size={16}
            aria-hidden="true"
          />
          <Input
            placeholder="Search your trainings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10 w-full"
            aria-label="Search trainings"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleSearch}
            size="sm"
            className="flex items-center gap-1.5"
          >
            <Search className="h-4 w-4" />
            Search
          </Button>

          <Button
            onClick={resetFilters}
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </Button>

          <LayoutSwitcher currentLayout={layout} onLayoutChange={setLayout} />
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="mb-6 border border-border/40 shadow-sm">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Category Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Select
                  value={selectedCategory || "ALL"}
                  onValueChange={handleCategoryFilter}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Level Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Level</label>
                <Select
                  value={selectedLevel || "ALL"}
                  onValueChange={handleLevelFilter}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Levels</SelectItem>
                    {levels.map((level) => (
                      <SelectItem key={level} value={level}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Format Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Format</label>
                <Select
                  value={selectedFormat || "ALL"}
                  onValueChange={handleFormatFilter}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Formats" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Formats</SelectItem>
                    {formats.map((format) => (
                      <SelectItem key={format} value={format}>
                        {format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters */}
            {(selectedCategory || selectedLevel || selectedFormat) && (
              <div className="mt-4 pt-4 border-t border-border/40">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm font-medium text-muted-foreground">Active filters:</span>

                  {selectedCategory && (
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10"
                    >
                      Category: {selectedCategory}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => handleCategoryFilter("ALL")}
                      />
                    </Badge>
                  )}

                  {selectedLevel && (
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10"
                    >
                      Level: {selectedLevel}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => handleLevelFilter("ALL")}
                      />
                    </Badge>
                  )}

                  {selectedFormat && (
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1 bg-primary/5 hover:bg-primary/10"
                    >
                      Format: {selectedFormat}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => handleFormatFilter("ALL")}
                      />
                    </Badge>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="h-7 px-2 text-xs"
                  >
                    Clear all
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trainings List */}
      <div className="mb-6">
        {myTrainingsError && (
          <div className="p-4 mb-4 text-destructive bg-destructive/10 rounded-md flex items-center gap-2">
            <X className="h-4 w-4" />
            Error loading trainings: {myTrainingsError}
          </div>
        )}

        {myTrainingsLoading ? (
          <div className={`grid grid-cols-1 ${layout === "grid" ? "sm:grid-cols-2 lg:grid-cols-3" : ""} gap-6`}>
            {Array(6)
              .fill(0)
              .map((_, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className={layout === "grid" ? "" : "flex flex-col md:flex-row"}>
                    <div className={layout === "grid" ? "" : "md:w-1/3"}>
                      <Skeleton className="h-48 w-full" />
                    </div>
                    <div className={layout === "grid" ? "p-6" : "flex-1 p-6"}>
                      <Skeleton className="h-6 w-3/4 mb-4" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-full mb-4" />
                      <div className="flex gap-2 mb-4">
                        <Skeleton className="h-6 w-20" />
                        <Skeleton className="h-6 w-20" />
                      </div>
                      <div className="flex justify-between items-center">
                        <Skeleton className="h-6 w-16" />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
          </div>
        ) : myTrainingsList.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="flex flex-col items-center justify-center py-12">
              <BookOpen className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No trainings found</h3>
              <p className="text-muted-foreground mb-6">
                You haven't created any trainings yet or none match your current filters.
              </p>
              <Button onClick={handleCreateTraining}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Training
              </Button>
            </div>
          </Card>
        ) : (
          <div className={`grid grid-cols-1 ${layout === "grid" ? "sm:grid-cols-2 lg:grid-cols-3" : ""} gap-6`}>
            {myTrainingsList.map((training) => renderTraining(training))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {myTrainingsPaginationMeta && myTrainingsPaginationMeta.totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handlePageChange(myTrainingsPaginationMeta.currentPage - 1)}
                  className={
                    myTrainingsPaginationMeta.currentPage <= 1
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                />
              </PaginationItem>

              {Array.from(
                { length: myTrainingsPaginationMeta.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => handlePageChange(page)}
                    isActive={page === myTrainingsPaginationMeta.currentPage}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(myTrainingsPaginationMeta.currentPage + 1)}
                  className={
                    myTrainingsPaginationMeta.currentPage >= myTrainingsPaginationMeta.totalPages
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the training "{trainingToDelete?.title}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyTrainings;
